import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, PhoneCall, RotateCcw, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

export function BackOfficeEnrichmentCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/enrichment/status');
      setStatus(data);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const runDirigeants = async () => {
    if (!window.confirm("Lancer maintenant l'enrichissement des dirigeants manquants ?\n\nLe traitement utilise Google + Pappers et vérifie le code NAF supermarché. Il s'exécute en arrière-plan (peut prendre plusieurs minutes).")) return;
    setRunning(true);
    try {
      await api.post('/enrichment/retry-dirigeants');
      toast.success("Enrichissement dirigeants démarré en arrière-plan");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setRunning(false);
  };

  const runPhones = async () => {
    if (!window.confirm("Lancer maintenant l'enrichissement des téléphones manquants ?")) return;
    setRunning(true);
    try {
      await api.post('/enrichment/retry-phones');
      toast.success("Enrichissement téléphones démarré en arrière-plan");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setRunning(false);
  };

  const sendSummary = async () => {
    if (!window.confirm("Envoyer maintenant l'email de synthèse du dernier run d'enrichissement aux super_admin + admin ?")) return;
    setRunning(true);
    try {
      const { data } = await api.post('/enrichment/send-summary-now');
      if (data.sent) toast.success(`Synthèse envoyée — ${data.found}/${data.total} dirigeants`);
      else toast.warning(data.reason || "Aucun run à synthétiser");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setRunning(false);
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <Card className="border-2 border-[#8B5CF6]/20 shadow-sm bg-gradient-to-br from-[#F5F3FF] to-white" data-testid="backoffice-enrichment-card">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#8B5CF6]/15 flex items-center justify-center flex-shrink-0">
              <RotateCcw className="w-5 h-5 text-[#8B5CF6]" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Enrichissement back-office</h3>
              <p className="text-xs text-[#71717A] mt-0.5">
                Relance automatique <strong>chaque lundi 04h00</strong> via Google + Pappers (filtrage NAF supermarché).
                Cible les prospects sans dirigeant : Enseigne + Ville → SIREN → vérification CP + NAF.
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={sendSummary} disabled={running}
              className="h-9 text-xs" data-testid="send-summary-btn">
              <Mail className="w-3.5 h-3.5 mr-1" />Email synthèse
            </Button>
            <Button size="sm" variant="outline" onClick={runPhones} disabled={running}
              className="h-9 text-xs" data-testid="run-phones-btn">
              <PhoneCall className="w-3.5 h-3.5 mr-1" />Téléphones manquants
            </Button>
            <Button size="sm" onClick={runDirigeants} disabled={running}
              className="h-9 text-xs bg-[#8B5CF6] hover:bg-[#7C3AED] text-white"
              data-testid="run-dirigeants-btn">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <UserCheck className="w-3.5 h-3.5 mr-1" />}
              Relancer dirigeants
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-xs text-[#71717A]">Chargement...</p>
        ) : status && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border border-[#E5E7EB] bg-white">
              <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold">Dirigeants manquants</p>
              <p className="text-2xl font-bold text-[#8B5CF6] mt-1" data-testid="missing-dirigeants-count">
                {status.missing_dirigeants_count}
              </p>
              <p className="text-[11px] text-[#71717A] mt-1">
                Dernière relance : {status.last_dirigeants_run
                  ? `${fmtDate(status.last_dirigeants_run.ended_at || status.last_dirigeants_run.started_at)} — ${status.last_dirigeants_run.found || 0}/${status.last_dirigeants_run.total || 0} trouvés`
                  : 'jamais'}
              </p>
            </div>
            <div className="p-3 rounded-lg border border-[#E5E7EB] bg-white">
              <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold">Téléphones manquants</p>
              <p className="text-2xl font-bold text-[#0EA5E9] mt-1" data-testid="missing-phones-count">
                {status.missing_phones_count}
              </p>
              <p className="text-[11px] text-[#71717A] mt-1">
                Dernière relance : {status.last_phones_run
                  ? `${fmtDate(status.last_phones_run.ended_at || status.last_phones_run.started_at)} — ${status.last_phones_run.found || 0}/${status.last_phones_run.total || 0} trouvés`
                  : 'jamais'}
              </p>
            </div>
            <div className="sm:col-span-2 p-3 rounded-lg border border-dashed border-[#8B5CF6]/30 bg-[#F5F3FF]">
              <p className="text-[11px] text-[#71717A]">
                <strong className="text-[#8B5CF6]">Prochaine exécution automatique :</strong>{' '}
                {fmtDate(status.next_scheduled_run_paris)} (Europe/Paris)
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
